import React, { ReactNode, useCallback } from 'react';
import { TFunction, withTranslation, WithTranslation } from 'next-i18next';
import { FieldErrors, RegisterOptions, useFormContext } from 'react-hook-form';

import Legend from '~/components/form-elements/legend';
import InlineError from '~/components/form-elements/inline-error';
import Label from '~/components/form-elements/label';
import HintText from '~/components/form-elements/hint-text';
import FormGroup from '~/components/form-elements/form-group';
import Fieldset from '~/components/form-elements/fieldset';

import { FlexedFormFields, FormDateTextField } from './styles';
import HintExpander from '~/components/form-elements/hint-expander';
import { buildErrorMsgInterpolationOptions } from '~/utils/helpers';

interface DateInputProps extends WithTranslation {
    t: TFunction;
    leadLabelText: string;
    name: string;
    labelSize?: 'default' | 'large';
    hintExpander?: boolean;
    hintExpanderContent?: string | ReactNode;
    hintExpanderLabel?: string | null;
    hintId?: string;
    hintText?: string | null;
    defaultYear?: number;
    defaultMonth?: number;
    defaultDay?: number;
    registerOptions?: RegisterOptions;
    registerYearOptions?: RegisterOptions;
    registerMonthOptions?: RegisterOptions;
    registerDayOptions?: RegisterOptions;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    errors: FieldErrors;
    showHintExpanderOnError?: boolean;
}

const DateInput = ({
    t,
    leadLabelText,
    name,
    labelSize = 'default',
    hintExpander = false,
    hintExpanderContent,
    hintExpanderLabel,
    hintId,
    hintText,
    defaultYear,
    defaultMonth,
    defaultDay,
    registerOptions,
    registerYearOptions,
    registerMonthOptions,
    registerDayOptions,
    required = true,
    disabled = false,
    readOnly = false,
    errors,
    showHintExpanderOnError = false,
}: DateInputProps) => {
    const dobYearError = errors && {}.hasOwnProperty.call(errors, `${name}Year`);
    const dobMonthError = errors && {}.hasOwnProperty.call(errors, `${name}Month`);
    const dobDayError = errors && {}.hasOwnProperty.call(errors, `${name}Day`);
    const dobError = errors && {}.hasOwnProperty.call(errors, `${name}`);
    const { trigger, formState, clearErrors, register } = useFormContext();

    const handleChange = useCallback(
        (maxLength: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.currentTarget.value.length > e.currentTarget.maxLength) {
                e.currentTarget.value = e.currentTarget.value.slice(0, maxLength);
            }

            const dobField = document.getElementById(name) as HTMLInputElement;
            const dobYearField = document.getElementById(`${name}Year`) as HTMLInputElement;
            const dobMonthField = document.getElementById(`${name}Month`) as HTMLInputElement;
            const dobDayField = document.getElementById(`${name}Day`) as HTMLInputElement;

            // Always build and set the date value from current field values
            const newDate = `${dobYearField?.value || ''}-${dobMonthField?.value || ''}-${dobDayField?.value || ''}`;
            dobField?.setAttribute('value', newDate);

            // Only trigger the combined date validation if there are no individual field errors
            // Individual field validation is handled automatically by React Hook Form
            if (formState?.submitCount > 0) {
                // Defer the combined date validation to avoid stale error state
                // and only trigger if individual fields are valid
                setTimeout(() => {
                    // Re-check current error state inside setTimeout to avoid stale closure values
                    const currentYearError =
                        errors && {}.hasOwnProperty.call(errors, `${name}Year`);
                    const currentMonthError =
                        errors && {}.hasOwnProperty.call(errors, `${name}Month`);
                    const currentDayError = errors && {}.hasOwnProperty.call(errors, `${name}Day`);

                    [`${name}Year`, `${name}Month`, `${name}Day`].forEach(fieldName => {
                        if (fieldName !== e.target?.name) {
                            trigger(fieldName);
                        }
                    });

                    if (!currentYearError && !currentMonthError && !currentDayError) {
                        trigger(name);
                    }
                }, 0);
            }

            // Clear the main date error if it exists (will be re-validated)
            if (errors[name]) {
                clearErrors(name);
            }
        },
        [clearErrors, errors, formState?.submitCount, name, trigger]
    );

    const yearHintId = [hintId, 'dob-year-hint'].filter(Boolean).join(' ');
    const monthHintId = [hintId, 'dob-month-hint'].filter(Boolean).join(' ');
    const dayHintId = [hintId, 'dob-day-hint'].filter(Boolean).join(' ');

    return (
        <div id="formgroup-dob">
            <Fieldset
                role="group"
                aria-describedby={[
                    hintText ? hintId : undefined,
                    `date-of-birth-hint`,
                    dobError ? 'dob-error' : undefined,
                    dobYearError ? `dob-year-error` : undefined,
                    dobMonthError ? `dob-month-error` : undefined,
                    dobDayError ? `dob-day-error` : undefined,
                ].join(' ')}
            >
                <FormGroup
                    errors={
                        {}.hasOwnProperty.call(errors, `${name}`) ||
                        {}.hasOwnProperty.call(errors, `${name}Year`) ||
                        {}.hasOwnProperty.call(errors, `${name}Month`) ||
                        {}.hasOwnProperty.call(errors, `${name}Day`)
                    }
                >
                    <Legend size="default" addRequiredFlag={required}>
                        {leadLabelText}
                    </Legend>

                    {/* errors */}
                    {dobError ? (
                        <InlineError id="dob-error" key={`${name}`}>
                            {t(
                                `errors:errorCodes.${(errors[`${name}`]?.message as string).split(',')[0]}`,
                                buildErrorMsgInterpolationOptions(
                                    errors[`${name}`]?.message as string
                                )
                            )}
                        </InlineError>
                    ) : null}

                    {dobYearError ? (
                        <InlineError id="dob-year-error" key={`${name}Year`}>
                            {t(
                                `errors:errorCodes.${(errors[`${name}Year`]?.message as string).split(',')[0]}`,
                                buildErrorMsgInterpolationOptions(
                                    errors[`${name}Year`]?.message as string
                                )
                            )}
                        </InlineError>
                    ) : null}

                    {dobMonthError ? (
                        <InlineError id="dob-month-error" key={`${name}Month`}>
                            {t(
                                `errors:errorCodes.${(errors[`${name}Month`]?.message as string).split(',')[0]}`,
                                buildErrorMsgInterpolationOptions(
                                    errors[`${name}Month`]?.message as string
                                )
                            )}
                        </InlineError>
                    ) : null}

                    {dobDayError ? (
                        <InlineError id="dob-day-error" key={`${name}Day`}>
                            {t(
                                `errors:errorCodes.${(errors[`${name}Day`]?.message as string).split(',')[0]}`,
                                buildErrorMsgInterpolationOptions(
                                    errors[`${name}Day`]?.message as string
                                )
                            )}
                        </InlineError>
                    ) : null}

                    {hintText && hintId && <HintText id={hintId}>{hintText}</HintText>}

                    {/* Hint text fields for individual inputs, only visible by screen reader */}
                    <HintText id="dob-year-hint" readonly>
                        {t('common:form.fields.dob.year.hintText')}
                    </HintText>
                    <HintText id="dob-month-hint" readonly>
                        {t('common:form.fields.dob.month.hintText')}
                    </HintText>
                    <HintText id="dob-day-hint" readonly>
                        {t('common:form.fields.dob.day.hintText')}
                    </HintText>

                    <FlexedFormFields id={`dob-flex-fields-${name}`}>
                        <div>
                            {/* this hidden field is required to display common error messages 
                            of all date fields and highlight all the error date fields in red color */}
                            <FormDateTextField
                                id={name}
                                $errors={dobError}
                                type="hidden"
                                {...register(name, {
                                    ...registerOptions,
                                })}
                            />
                            <Label
                                id="year"
                                addRequiredFlag={false}
                                size={labelSize}
                                showRequirementFlag={false}
                            >
                                {t('common:form.fields.dob.year.text')}
                            </Label>
                            <FormDateTextField
                                aria-describedby={yearHintId}
                                aria-labelledby={dobYearError ? `dob-year-error` : ''}
                                aria-invalid={{}.hasOwnProperty.call(errors, `${name}Year`)}
                                id={`${name}Year`}
                                pattern="[0-9]{4}"
                                inputMode="numeric"
                                defaultValue={defaultYear}
                                data-errortitle={t('common:form.fields.dob.year.errorTitle')}
                                maxLength={4}
                                disabled={disabled}
                                readOnly={readOnly}
                                $errors={dobYearError || dobError}
                                {...register(`${name}Year`, {
                                    ...registerYearOptions,
                                    onChange: handleChange(4),
                                })}
                            />
                        </div>
                        <div>
                            <Label
                                id="month"
                                addRequiredFlag={false}
                                size={labelSize}
                                showRequirementFlag={false}
                            >
                                {t('common:form.fields.dob.month.text')}
                            </Label>
                            <FormDateTextField
                                aria-describedby={monthHintId}
                                aria-labelledby={dobMonthError ? `dob-month-error` : ''}
                                aria-invalid={{}.hasOwnProperty.call(errors, `${name}Month`)}
                                id={`${name}Month`}
                                pattern="[0-9]{1,2}"
                                inputMode="numeric"
                                defaultValue={defaultMonth}
                                data-errortitle={t('common:form.fields.dob.month.errorTitle')}
                                maxLength={2}
                                disabled={disabled}
                                readOnly={readOnly}
                                $errors={dobMonthError || dobError}
                                {...register(`${name}Month`, {
                                    ...registerMonthOptions,
                                    onChange: handleChange(2),
                                })}
                            />
                        </div>
                        <div>
                            <Label
                                id="day"
                                addRequiredFlag={false}
                                size={labelSize}
                                showRequirementFlag={false}
                            >
                                {t('common:form.fields.dob.day.text')}
                            </Label>
                            <FormDateTextField
                                aria-describedby={dayHintId}
                                aria-labelledby={dobDayError ? ` dob-day-error` : ''}
                                id={`${name}Day`}
                                pattern="[0-9]{1,2}"
                                inputMode="numeric"
                                defaultValue={defaultDay}
                                data-errortitle={t('common:form.fields.dob.day.errorTitle')}
                                maxLength={2}
                                disabled={disabled}
                                readOnly={readOnly}
                                $errors={dobDayError || dobError}
                                {...register(`${name}Day`, {
                                    ...registerDayOptions,
                                    onChange: handleChange(2),
                                })}
                            />
                        </div>
                    </FlexedFormFields>

                    {hintExpander && (
                        <HintExpander
                            id={hintId ?? 'dateInputHintExpander'}
                            label={hintExpanderLabel ?? ''}
                            content={hintExpanderContent}
                            type="image"
                            isExpanded={showHintExpanderOnError && !!errors}
                        />
                    )}
                </FormGroup>
            </Fieldset>
        </div>
    );
};

export default withTranslation(['common', 'errors'])(DateInput);
